import React, { useCallback, useMemo, useState } from 'react';
import {
    TagPicker,
    TagPickerList,
    TagPickerInput,
    TagPickerControl,
    TagPickerOption,
    TagPickerGroup,
    useTagPickerFilter,
    Field,
    Spinner,
    Text
} from '@fluentui/react-components';
import RequiredLabel from '../RequiredLabel';
import { TagWithDismiss, useCompactDropdownStyles } from './TagWithDismiss';
import { DefaultOptionRenderer } from './DefaultOptionRenderer';
import {
    GenericTagPickerProps,
    TagPickerItem,
    noMatchesToken
} from './GenericTagPicker.types';
import { useTagPickerSearch } from './useTagPickerSearch';
import { useTagPickerSelection } from './useTagPickerSelection';
import { tagPickerStyles } from './GenericTagPicker.styles';

const GenericTagPicker = <T extends TagPickerItem, TContext = unknown>({
    selectedItem,
    selectedItems = [],
    multiSelect = false,
    disabled = false,
    isLoading = false,
    error,
    label,
    placeholder,
    required = false,
    searchFunction,
    prepopulateFunction,
    searchContext,
    minSearchLength = 3,
    searchDebounceMs = 300,
    enableWildcardSearch = true, // Default to enabled for backward compatibility
    onItemChange,
    onItemsChange,
    renderOption,
    hideCoin = false,
    customAvatar,
    isContact = false
}: GenericTagPickerProps<T, TContext>) => {

    // State to track if input has been focused (for single select UX)
    const [hasInputBeenFocused, setHasInputBeenFocused] = useState(false);

    const {
        items,
        isLoadingItems,
        isSearching,
        query,
        setQuery,
        isDropdownOpen,
        setIsDropdownOpen,
        hasCalledInitialFocus,
        setHasCalledInitialFocus
    } = useTagPickerSearch({
        searchFunction,
        prepopulateFunction,
        searchContext,
        minSearchLength,
        searchDebounceMs,
        enableWildcardSearch
    });

    const { handleOptionSelect, handleDismiss } = useTagPickerSelection({
        items,
        selectedItems,
        selectedItem,
        multiSelect,
        onItemChange,
        onItemsChange,
        setQuery
    });

    // Reset focus state when selection changes (for single select)
    React.useEffect(() => {
        if (!multiSelect && selectedItem) {
            setHasInputBeenFocused(false);
        }
    }, [selectedItem, multiSelect]);

    const compactDropdownClass = useCompactDropdownStyles();

    const onOpenChange = useCallback((_event: unknown, data: { open: boolean }) => {
        setIsDropdownOpen(data.open);

        if (!hasCalledInitialFocus && data.open) {
            setHasCalledInitialFocus(true);
        }
    }, [hasCalledInitialFocus, setIsDropdownOpen, setHasCalledInitialFocus]);

    const inputOnChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const searchValue = e.target.value;
        setQuery(searchValue);
    }, [setQuery]);

    const inputOnFocus = useCallback(() => {
        setHasInputBeenFocused(true);
    }, []);

    const inputOnBlur = useCallback(() => {
        // Only reset focus state if there's no query and no dropdown open
        if (!query && !isDropdownOpen) {
            setHasInputBeenFocused(false);
        }
    }, [query, isDropdownOpen]);

    const selectedKeys = useMemo(() => {
        if (multiSelect) {
            return selectedItems.map(item => item.id.toString());
        } else {
            return selectedItem ? [selectedItem.id.toString()] : [];
        }
    }, [multiSelect, selectedItems, selectedItem]);

    const actualPlaceholder = disabled ? `${label} selection disabled` : placeholder || `Search for ${label.toLowerCase()}...`;
    
    // Hide input in single select mode when there's a selection and user hasn't focused
    const shouldHideInput = !multiSelect && selectedItem && !hasInputBeenFocused && !query;
    
    const inputStyle = {
        ...tagPickerStyles.tagPickerInput,
        ...(shouldHideInput ? { 
            opacity: 0, 
            width: 0, 
            minWidth: 0, 
            padding: 0,
            border: 'none'
        } : {})
    };
    
    const noResultsText = isSearching ? "Searching..." : "No results found";

    // Reusable tag renderer with dismiss functionality
    const renderTag = (item: T, isMultiSelect: boolean) => (
        <TagWithDismiss
            key={item.key ?? item.id}
            item={item}
            isMultiSelect={isMultiSelect}
            disabled={disabled}
            hideCoin={hideCoin}
            isContact={isContact}
            customAvatar={customAvatar}
            onDismiss={handleDismiss}
        />
    );

    // Default option renderer - content for dropdown options (not Tags)
    const defaultRenderOption = (item: T) => (
        <DefaultOptionRenderer
            item={item}
            hideCoin={hideCoin}
            customAvatar={customAvatar}
        />
    );

    const tagPickerChildren = useTagPickerFilter({
        query,
        options: items.map(item => item.id.toString()),
        noOptionsElement: (
            <TagPickerOption value={noMatchesToken} text={noResultsText}>
                {noResultsText}
            </TagPickerOption>
        ),
        renderOption: (optionKey) => {
            const item = items.find(i => i.id.toString() === optionKey);
            if (!item) return <></>;

            return (
                <TagPickerOption key={item.id} value={item.id.toString()} text={item.displayText}>
                    {renderOption ? renderOption(item) : defaultRenderOption(item)}
                </TagPickerOption>
            );
        },
        filter: (optionKey) => {
            // Don't show already selected items
            return !selectedKeys.includes(optionKey);
        },
    });

    const isComponentLoading = isLoading || isLoadingItems || isSearching;

    return (
        <Field
            label={<RequiredLabel text={label} required={required} />}
            validationMessage={error || undefined}
            validationState={error ? "error" : "none"}
        >
            {isComponentLoading ? (
                <div style={tagPickerStyles.loadingContainer}>
                    <Spinner size="small" />
                    <Text>Loading...</Text>
                </div>
            ) : (
                <TagPicker
                    disabled={disabled}
                    positioning="below"
                    onOptionSelect={handleOptionSelect}
                    selectedOptions={selectedKeys}
                    onOpenChange={onOpenChange}
                    open={isDropdownOpen}
                >
                    <TagPickerControl expandIcon={null} style={tagPickerStyles.tagPickerControl}>
                        <TagPickerGroup style={tagPickerStyles.tagPickerGroup}>
                            {multiSelect
                                ? selectedItems.map((item) => renderTag(item, true))
                                : selectedItem && renderTag(selectedItem, false)
                            }
                        </TagPickerGroup>
                        <TagPickerInput
                            placeholder={shouldHideInput ? "" : actualPlaceholder}
                            value={query}
                            disabled={disabled}
                            onChange={inputOnChange}
                            onFocus={inputOnFocus}
                            onBlur={inputOnBlur}
                            autoComplete="off"
                            style={inputStyle}
                        />
                    </TagPickerControl>
                    <TagPickerList className={compactDropdownClass}>
                        {tagPickerChildren}
                    </TagPickerList>
                </TagPicker>
            )}
        </Field>
    );
};

export type { TagPickerItem, SearchFunction, PrepopulateFunction, GenericTagPickerProps } from './GenericTagPicker.types';
export default GenericTagPicker;
